import { makeStyles } from "@material-ui/core/styles";

// feat_347 — estilos compartidos de /Settings.
// Extraídos VERBATIM del useStyles que vivía en Settings/index.js (líneas 43-88)
// más las clases nuevas de la barra de tabs. Los 8 componentes de tab reciben
// `classes` por el ctx del padre: NO montar otro useStyles por tab.
const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		alignItems: "center",
		// feat_351 — DENSIDAD (punto 8 del patrón). Era theme.spacing(8, 8, 3) =
		// 64px arriba y a los lados: con el Container maxWidth="md" adentro, esos
		// 64px laterales son aire muerto que le come ancho útil a la grilla de 2
		// columnas. 24px arriba / 32px a los lados / 32px abajo. Afecta a LOS 8
		// TABS (la clase es del shell, no del tab).
		padding: theme.spacing(3, 4, 4),
	},
	paper: {
		padding: theme.spacing(2),
		display: "flex",
		alignItems: "center",
		marginBottom: 12,
	},
	settingOption: {
		marginLeft: "auto",
	},
	margin: {
		margin: theme.spacing(1),
	},
	sectionTitle: {
		marginTop: theme.spacing(3),
		marginBottom: theme.spacing(1),
	},
	saveButton: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(2),
	},
	codeBlock: {
		backgroundColor: theme.palette.type === "dark" ? "#1a1a2e" : "#f4f4f8",
		color: theme.palette.type === "dark" ? "#a8d8a8" : "#1a1a1a",
		fontFamily: "monospace",
		fontSize: 12,
		padding: theme.spacing(1.5),
		borderRadius: 4,
		overflowX: "auto",
		whiteSpace: "pre",
		position: "relative",
	},
	endpointChip: {
		marginRight: theme.spacing(1),
		fontFamily: "monospace",
		fontWeight: 700,
	},
	devSection: {
		marginTop: theme.spacing(2),
	},

	// --- feat_347: barra de tabs ---------------------------------------------
	// El indicador va en `primary` (#2576d2) porque ES la convención de tabs del
	// proyecto: TicketsManager, Contacts, ContactDrawer y MetaTemplatesDrawer usan
	// todos indicatorColor/textColor="primary". El verde #25D366 en este código
	// está reservado a indicadores de estado (IA activa), no a navegación.
	tabsHeader: {
		position: "sticky",
		top: 0,
		zIndex: 3,
		backgroundColor: theme.palette.background.paper,
		marginBottom: theme.spacing(1),
	},

	// === feat_351 — PATRÓN VISUAL CANÓNICO DE LOS TABS ========================
	// Estas clases las consumen los componentes de `components/SettingsPattern.js`
	// (TabHeader / SettingsCard / SectionHeader / SectionDivider / ToggleRow /
	// FieldGrid / FieldCell). Un tab NO debería necesitar ninguna de estas clases
	// a mano: importa los componentes. Están acá, y no en un useStyles propio del
	// archivo de componentes, para que TODO el look de /Settings se lea de un solo
	// archivo (y para no montar una segunda hoja JSS).
	//
	// ⚠ Nada de este patrón depende de montaje/desmontaje: son clases y markup
	// estáticos. La REGLA DURA de feat_347 (los 8 TabPanel quedan SIEMPRE
	// montados) queda intacta.

	// feat_352 — Título de la PANTALLA: <Typography variant="h4" component="h1">.
	// Es el único h1 de /Settings. La jerarquía queda h4/h1 (pantalla) > h5/h2
	// (tabTitle) > subtitle1/h3 (sectionHeading): el título de pantalla tiene que
	// pesar MÁS que el título de tab. Antes de feat_352 era un `body2`, o sea más
	// chico que el subtitle1 de una sección interna.
	screenTitle: {
		fontWeight: 500,
		marginBottom: theme.spacing(1),
	},

	// Título del tab: <Typography variant="h5" component="h2">.
	tabTitle: {
		marginBottom: theme.spacing(2),
		fontWeight: 500,
	},
	tabTitleDescription: {
		display: "block",
		marginTop: theme.spacing(-1.5),
		marginBottom: theme.spacing(2),
	},

	// UNA Paper por SECCIÓN TEMÁTICA — nunca una por opción individual.
	sectionCard: {
		padding: theme.spacing(2, 2.5),
		marginBottom: theme.spacing(2),
	},

	// Título de sección: <Typography variant="subtitle1" component="h3">.
	sectionHeading: {
		fontWeight: 600,
		lineHeight: 1.4,
	},
	sectionDescription: {
		display: "block",
		marginTop: theme.spacing(0.5),
		marginBottom: theme.spacing(1),
	},
	// Separador ENTRE secciones dentro de una misma Paper.
	sectionDivider: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},

	// --- Fila de toggle, estilo WhatsApp: label a la IZQUIERDA, Switch a la
	// DERECHA. NO es el FormControlLabel de MUI (que pone el switch adelante).
	toggleRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		padding: theme.spacing(1, 0),
		// Hairline entre filas consecutivas de la misma sección. No hace falta
		// meter <Divider> a mano entre toggles.
		"& + &": {
			borderTop: `1px solid ${theme.palette.divider}`,
		},
	},
	toggleRowText: {
		display: "flex",
		flexDirection: "column",
		minWidth: 0,
	},
	toggleRowLabelLine: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(0.5),
	},
	toggleRowLabel: {
		// El label es un <label htmlFor> real: clickearlo tiene que verse clickeable.
		cursor: "pointer",
	},
	toggleRowHelpButton: {
		padding: 2,
		color: theme.palette.text.secondary,
	},
	// La ayuda corta es UNA línea: si no entra, se corta con elipsis y el texto
	// largo vive en el tooltip. Que el layout no crezca es parte del patrón.
	toggleRowHelp: {
		display: "block",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
	},
	toggleRowSwitch: {
		flexShrink: 0,
	},

	tab: {
		minWidth: 120,
		textTransform: "none",
		fontSize: 13,
		// Mismo refuerzo del tab activo que TicketsManager: además de la línea,
		// fondo azul claro + negrita.
		"&.Mui-selected": {
			backgroundColor: "rgba(25, 118, 210, 0.08)",
			fontWeight: 700,
		},
	},
}));

export default useStyles;
